import type { SourceTask, TaskSummary } from "../types";

/**
 * Collapses a tag list to the first spelling seen of each tag. Comparison is
 * case-insensitive and ignores surrounding whitespace; blank entries drop out.
 */
export const dedupeTags = (
  tags: ReadonlyArray<string | undefined | null>,
): string[] =>
  tags.reduce<string[]>((acc, tag) => {
    const normalised = (tag || "").trim();
    if (!normalised) {
      return acc;
    }

    if (
      !acc.some((existing) => existing.toLowerCase() === normalised.toLowerCase())
    ) {
      acc.push(normalised);
    }

    return acc;
  }, []);

/**
 * The colour the board sets for a tag, matched the way tags are everywhere
 * else: ignoring case and surrounding whitespace.
 *
 * Case has to be ignored for more than consistency. The API camel-cased these
 * keys on the way out, so a colour set for "Bug" came back as "bug" and was
 * saved that way on the next settings save — boards carry keys like that.
 *
 * @param tagColours The board's tag colours, keyed by tag.
 * @param tag The tag to colour.
 * @returns The colour, or `undefined` when the board sets none.
 */
export const getTagColour = (
  tagColours: Readonly<Record<string, string>> | undefined,
  tag: string,
): string | undefined => {
  if (!tagColours) {
    return undefined;
  }

  const exact = tagColours[tag];
  if (exact) {
    return exact;
  }

  const wanted = tag.trim().toLowerCase();
  const key = Object.keys(tagColours).find(
    (candidate) => candidate.trim().toLowerCase() === wanted,
  );

  return key === undefined ? undefined : tagColours[key];
};

/** A tag's colours: its board colour, and text that reads on it. */
export interface TagStyle {
  backgroundColor?: string;
  color?: string;
}

const isHexColourLight = (colour: string): boolean => {
  const hex = colour.replace("#", "");
  if (hex.length !== 6) {
    return false;
  }

  const red = Number.parseInt(hex.substring(0, 2), 16);
  const green = Number.parseInt(hex.substring(2, 4), 16);
  const blue = Number.parseInt(hex.substring(4, 6), 16);

  if ([red, green, blue].some((channel) => Number.isNaN(channel))) {
    return false;
  }

  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
  return brightness > 155;
};

/**
 * How a tag is painted wherever it appears — card, filter pane, task form — so
 * a light colour gets dark text everywhere rather than white on yellow in some.
 *
 * @param tagColours The board's tag colours, keyed by tag.
 * @param tag The tag to paint.
 * @returns Background and text colour, or nothing when the board sets none.
 */
export const getTagStyle = (
  tagColours: Readonly<Record<string, string>> | undefined,
  tag: string,
): TagStyle => {
  const backgroundColor = getTagColour(tagColours, tag);
  if (!backgroundColor) {
    return {};
  }

  return {
    backgroundColor,
    color: isHexColourLight(backgroundColor) ? "#1b1a19" : "#ffffff",
  };
};

/**
 * Tags a task inherits from the sources it is linked to. Pushed source tasks
 * are this app's own copy of the task in the other system, so their tags came
 * from here in the first place and would double up.
 */
export const getSourceTaskTags = (
  sourceTasks?: ReadonlyArray<SourceTask>,
): string[] =>
  dedupeTags(
    (sourceTasks ?? [])
      .filter((sourceTask) => !sourceTask.isPushed)
      .flatMap((sourceTask) => sourceTask.tags ?? []),
  );

/**
 * Every tag a task shows on the board: its own first, then whatever its linked
 * sources add. This is the set the card renders, overflow included.
 */
export const mergeTaskTags = (
  taskTags?: ReadonlyArray<string>,
  sourceTasks?: ReadonlyArray<SourceTask>,
): string[] => dedupeTags([...(taskTags ?? []), ...getSourceTaskTags(sourceTasks)]);

/**
 * Source tags the task does not already carry itself — the ones that only
 * exist because of a connection, and so cannot be edited here.
 */
export const getInheritedSourceTags = (
  taskTags?: ReadonlyArray<string>,
  sourceTasks?: ReadonlyArray<SourceTask>,
): string[] => {
  const ownTags = new Set(
    (taskTags ?? []).map((tag) => (tag || "").trim().toLowerCase()),
  );

  return getSourceTaskTags(sourceTasks).filter(
    (tag) => !ownTags.has(tag.toLowerCase()),
  );
};

/**
 * The distinct tags in use across a set of tasks, sorted for display.
 *
 * Source tags count, exactly as they do on a card — a Jira label is a tag to
 * whoever is filtering by it.
 *
 * Always pass the *unfiltered* tasks: computing this from the filtered set
 * would remove every other tag from the list the moment one was selected.
 *
 * @param tasks The board's tasks.
 * @returns Distinct tags, sorted case-insensitively.
 */
export const collectTaskTags = (tasks: ReadonlyArray<TaskSummary>): string[] =>
  dedupeTags(
    tasks.flatMap((task) => mergeTaskTags(task.tags, task.sourceTasks)),
  ).sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" }),
  );

/**
 * Whether a task carries any of the given tags.
 *
 * Any rather than all: picking two tags widens the board to tasks carrying
 * either, rather than narrowing it to tasks carrying both. Matching ignores
 * case, so it agrees with how the options were collapsed.
 *
 * @param task The task to test.
 * @param tags The selected tags. An empty list matches everything.
 * @returns `true` when the task should be shown.
 */
export const taskMatchesTagFilters = (
  task: TaskSummary,
  tags: ReadonlyArray<string>,
): boolean => {
  if (tags.length === 0) {
    return true;
  }

  const taskTags = mergeTaskTags(task.tags, task.sourceTasks).map((tag) =>
    tag.toLowerCase(),
  );

  return tags.some((tag) => taskTags.includes(tag.trim().toLowerCase()));
};
